import type { ProductOptimization } from '~/types/enrichment';

import { attributePriorityRank } from './attribute-priority';

/** Impact values ordered by severity, most urgent first. `Low-Med` sits between
 *  Medium and Low; `Optimized` is last because it needs no attention. Unknown
 *  values sort after every known one rather than jumping to the top. */
export const IMPACT_ORDER = ['Critical', 'High', 'Medium', 'Low-Med', 'Low', 'Optimized'];

/** Severity rank for sorting. Unknown or absent impacts rank last. */
export function impactRank(impact: string | undefined): number {
  const index = impact ? IMPACT_ORDER.indexOf(impact) : -1;
  return index === -1 ? IMPACT_ORDER.length : index;
}

/** Sorts impact *values* by severity — for filter option lists. Unknown values
 *  keep a stable alphabetical order at the end. */
export function sortImpacts(impacts: string[]): string[] {
  return [...impacts].sort((a, b) => {
    const ai = IMPACT_ORDER.indexOf(a);
    const bi = IMPACT_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

/** Orders enriched attributes for display: priority attributes first (in
 *  `ATTRIBUTE_PRIORITY` order), then severity, then name. The enrichment mapper
 *  returns them alphabetically, which buries the Critical rows among the
 *  Mediums — every surface that lists attributes should use this so the same
 *  product reads the same way in the list, the detail page and quick view. */
export function sortByImpact<T extends Pick<ProductOptimization, 'impact' | 'label' | 'field'>>(
  optimizations: T[],
): T[] {
  return [...optimizations].sort(
    (a, b) =>
      attributePriorityRank(a.field, a.label) - attributePriorityRank(b.field, b.label) ||
      impactRank(a.impact) - impactRank(b.impact) ||
      a.label.localeCompare(b.label),
  );
}

/** Resolves the Tailwind class string for an optimization's impact badge. */
export function getImpactBadgeClass(impact?: string): string {
  if (impact === 'Critical' || impact === 'Optimized') {
    return 'border-green-200 bg-green-50 text-green-700';
  }
  if (impact === 'High') return 'border-blue-200 bg-blue-50 text-blue-700';
  if (impact === 'Medium') return 'border-amber-200 bg-amber-50 text-amber-700';
  return 'border-neutral-200 bg-neutral-50 text-neutral-500';
}
